import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { themes } from 'theme/themes';
import { brandingInfo } from 'features/server/brandingSlice';
import { useSelector } from 'react-redux';
import {
  normalizeMatrixRows,
  resolveEditablePriceColumnIndex,
  type PriceListSheetMatrix,
} from './priceListExcel';

type PriceListUploadPreviewTableProps = {
  matrix: PriceListSheetMatrix;
  onMatrixChange: (next: PriceListSheetMatrix) => void;
};

const HEADER_ROW = 0;

const normalizePreviewCellText = (value: unknown): string =>
  String(value ?? '')
    .normalize('NFKC')
    .replace(/\u00a0/g, ' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const rowLooksColumnHeader = (row: Array<unknown> | undefined): boolean => {
  if (!row || !row.length) return false;
  const normalized = row.map((cell) => normalizePreviewCellText(cell));
  const hasPriceTitle = normalized.some((cell) => cell === 'price' || cell === 'pl price');
  const hasNameOrCodeTitle = normalized.some((cell) => cell.includes('name') || cell.includes('code'));
  return hasPriceTitle && hasNameOrCodeTitle;
};

// ---------------------------------------------------------------------------
// EditableMatrixCell — local value + onChange commit (Price column only)
// ---------------------------------------------------------------------------
type EditableMatrixCellProps = {
  initialValue: string;
  onCommit: (val: string) => void;
};

const EditableMatrixCell: React.FC<EditableMatrixCellProps> = ({ initialValue, onCommit }) => {
  const [val, setVal] = React.useState(initialValue);

  const onCommitRef = React.useRef(onCommit);
  onCommitRef.current = onCommit;

  const prevInitialRef = React.useRef(initialValue);
  if (prevInitialRef.current !== initialValue) {
    prevInitialRef.current = initialValue;
    if (initialValue !== val) {
      setVal(initialValue);
    }
  }

  return (
    <TextField
      value={val}
      type="text"
      size="small"
      fullWidth
      variant="standard"
      inputProps={{ autoComplete: 'off' }}
      sx={{ '& input': { fontSize: '0.875rem' } }}
      onChange={(e) => {
        const newVal = e.target.value;
        setVal(newVal);
        onCommitRef.current(newVal);
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// PriceListUploadPreviewTable
// ---------------------------------------------------------------------------
export const PriceListUploadPreviewTable: React.FC<PriceListUploadPreviewTableProps> = ({
  matrix,
  onMatrixChange,
}) => {
  const { t } = useTranslation();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];

  const [editMatrix, setEditMatrix] = React.useState<PriceListSheetMatrix>(() =>
    normalizeMatrixRows(matrix)
  );

  React.useEffect(() => {
    setEditMatrix(normalizeMatrixRows(matrix));
  }, [matrix]);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const headerRow = editMatrix[HEADER_ROW] || [];
  const editablePriceCol = React.useMemo(
    () => resolveEditablePriceColumnIndex(headerRow),
    [headerRow]
  );
  const dataRowCount = Math.max(0, editMatrix.length - 1);
  const visibleDataStart = page * rowsPerPage;
  const paginatedSlice = editMatrix.slice(
    HEADER_ROW + 1 + visibleDataStart,
    HEADER_ROW + 1 + visibleDataStart + rowsPerPage
  );

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const updateCell = React.useCallback(
    (absoluteRow: number, col: number, value: string) => {
      setEditMatrix((prev) => {
        const next = normalizeMatrixRows(prev.map((row) => [...row]));
        if (!next[absoluteRow]) next[absoluteRow] = [];
        next[absoluteRow][col] = value;
        onMatrixChange(next);
        return next;
      });
    },
    [onMatrixChange]
  );

  return (
    <Box
      sx={{ width: '100%', '& input': { pointerEvents: 'auto' } }}
      className="reportsPreviewTable grid"
      id="pricelist-upload-preview-table"
    >
      <TableContainer>
        <Table sx={{ minWidth: 500 }} size="small" aria-label={t('Upload')}>
          <TableHead>
            <TableRow>
              <TableCell width={48}>#</TableCell>
              {headerRow.map((h, colIndex) => (
                <TableCell key={`h-${colIndex}`} sx={{ fontWeight: 700 }}>
                  {h === '' || h == null ? '—' : String(h)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {dataRowCount === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={Math.max(headerRow.length, 1) + 1}
                  align="center"
                  className="py-3"
                >
                  <Typography
                    className="font-Saira font-[500] text-[15px] italic"
                    style={{ color: themeColors?.primary }}
                  >
                    {t('noRecordFound')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedSlice.map((row, sliceIndex) => {
                const absoluteRow = HEADER_ROW + 1 + visibleDataStart + sliceIndex;
                return (
                  <TableRow hover key={`r-${absoluteRow}`}>
                    <TableCell>{visibleDataStart + sliceIndex + 1}</TableCell>
                    {headerRow.map((_, colIndex) => {
                      const raw = row ? row[colIndex] : undefined;
                      const display = raw === null || raw === undefined ? '' : String(raw);

                      const isEditablePrice =
                        editablePriceCol !== null &&
                        colIndex === editablePriceCol &&
                        !rowLooksColumnHeader(row);

                      return (
                        <TableCell
                          key={`c-${absoluteRow}-${colIndex}`}
                          sx={{ verticalAlign: 'middle', minWidth: 96 }}
                        >
                          {isEditablePrice ? (
                            <EditableMatrixCell
                              initialValue={display}
                              onCommit={(val) => updateCell(absoluteRow, colIndex, val)}
                            />
                          ) : (
                            <Typography component="span" sx={{ fontSize: '0.875rem' }}>
                              {display}
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {dataRowCount > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 15, 20, 25, 50, 100]}
          component="div"
          count={dataRowCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          className="hide-on-print"
          labelRowsPerPage={t('rowsPerPage')}
          showFirstButton
          showLastButton
        />
      )}
    </Box>
  );
};
